import {
  type Dealness,
  type Prediction,
  PredictionResultEnum,
  type RecommendationV2,
} from "@b2bportal/air-shopping-api";
import { getDatadogUserId } from "@hopper-b2b/datadog";
import { shouldForceNeutralPrediction } from "./pricePrediction";

/**
 * Shared Datadog markers for Flight Watch / price-alert shop entry.
 * NVH-661 = blank/broken hydration; NVH-1070 = push price vs live shop + buy CTA.
 */
export const PRICE_WATCH_DIAGNOSTICS = "NVH-661,NVH-1070";

export type PriceWatchEntrySource =
  | "watch_flag"
  | "deeplink_without_watch_flag"
  | "organic";

type QueryShape = {
  isFromFlightWatch?: boolean;
  origin?: string | null;
  destination?: string | null;
  originType?: string | null;
  destinationType?: string | null;
  departureDate?: Date | string | null;
  returnDate?: Date | string | null;
  tripCategory?: string | null;
  stopsOption?: string | null;
  noLCC?: boolean | null;
};

/**
 * Classifies how the user likely entered flight shop.
 * Push deeplinks (Nubank) omit `isFromFlightWatch` but still carry route+date.
 */
export function getPriceWatchEntrySource(
  query: QueryShape
): PriceWatchEntrySource {
  if (query.isFromFlightWatch) {
    return "watch_flag";
  }
  if (query.origin && query.destination && query.departureDate) {
    return "deeplink_without_watch_flag";
  }
  return "organic";
}

/** Safe shop-query fields for Datadog (codes/flags + Hopper UserId — no PII). */
export function buildFlightShopQueryDiagnostics(query: QueryShape) {
  const isFromFlightWatch = !!query.isFromFlightWatch;
  return {
    diagnostic: "NVH-661",
    diagnostics: PRICE_WATCH_DIAGNOSTICS,
    userId: getDatadogUserId(),
    isFromFlightWatch,
    entrySource: getPriceWatchEntrySource(query),
    requestedOriginCode: query.origin ?? null,
    requestedDestinationCode: query.destination ?? null,
    hasOriginType: !!query.originType,
    hasDestinationType: !!query.destinationType,
    hasDepartureDate: !!query.departureDate,
    hasReturnDate: !!query.returnDate,
    hasTripCategory: !!query.tripCategory,
    tripCategory: query.tripCategory ?? null,
    hasStopsOption: !!query.stopsOption,
    noLCC: query.noLCC ?? null,
  };
}

type PredictionLike =
  | (Prediction & { PredictionResult?: PredictionResultEnum })
  | {
      PredictionResult: PredictionResultEnum;
      recommendationV2?: RecommendationV2;
      recommendation?: string;
      dealness?: Dealness;
      lowestPrice?: { fiat?: { value?: number; currencyCode?: string } };
      predictionCopy?: {
        recommendationTitle?: string[];
        recommendationBody?: string[];
      };
    }
  | null
  | undefined;

const emptyPredictionDiagnostics = () => ({
  diagnostic: "NVH-1070",
  diagnostics: PRICE_WATCH_DIAGNOSTICS,
  userId: getDatadogUserId(),
  hasPrediction: false,
  predictionResult: null as PredictionResultEnum | null,
  recommendationV2: null as RecommendationV2 | null,
  dealness: null as Dealness | null,
  forceNeutralBuy: false,
  hasRecommendationCopy: false,
  hasRecommendationBodyCopy: false,
  lowestPriceAmount: null as number | null,
  lowestPriceCurrency: null as string | null,
});

/**
 * Safe prediction / buy-CTA signals for NVH-1070.
 *
 * `forceNeutralBuy` and the copy flags together separate the three ways the buy
 * indication can be absent: a legitimate Wait, the neutral override, and copy
 * the bar renders as an empty title. Only presence is logged, never the text.
 */
export function buildPricePredictionDiagnostics(prediction: PredictionLike) {
  if (!prediction) {
    return emptyPredictionDiagnostics();
  }

  const predictionResult =
    "PredictionResult" in prediction && prediction.PredictionResult
      ? prediction.PredictionResult
      : PredictionResultEnum.Prediction;

  if (predictionResult !== PredictionResultEnum.Prediction) {
    return { ...emptyPredictionDiagnostics(), predictionResult };
  }

  const recommendationV2 =
    "recommendationV2" in prediction ? prediction.recommendationV2 : undefined;
  const dealness = "dealness" in prediction ? prediction.dealness : undefined;
  const lowestFiat =
    "lowestPrice" in prediction ? prediction.lowestPrice?.fiat : undefined;
  const predictionCopy =
    "predictionCopy" in prediction ? prediction.predictionCopy : undefined;

  return {
    diagnostic: "NVH-1070",
    diagnostics: PRICE_WATCH_DIAGNOSTICS,
    userId: getDatadogUserId(),
    hasPrediction: true,
    predictionResult,
    recommendationV2: recommendationV2 ?? null,
    dealness: dealness ?? null,
    forceNeutralBuy: shouldForceNeutralPrediction(dealness, recommendationV2),
    hasRecommendationCopy: !!predictionCopy?.recommendationTitle?.[0],
    hasRecommendationBodyCopy: !!predictionCopy?.recommendationBody?.[0],
    lowestPriceAmount: lowestFiat?.value ?? null,
    lowestPriceCurrency: lowestFiat?.currencyCode ?? null,
  };
}
